import { Box, Stack, Typography } from '@mui/material'
import { useContext } from 'react'
import { Link } from 'react-router-dom'
import { ClientContext } from 'src/components/providers/ClientProvider'
import { corporateSiteUrl, kobutsuLicenseText, serviceName } from 'src/config'

export const Footer = () => {
  const { client, isLoading } = useContext(ClientContext)

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: 'background.dark',
        borderTop: '1px solid',
        borderTopColor: 'divider',
        mt: 6,
        // permanent drawer (240px) のぶん左にオフセット
        pl: { xs: 0, md: client ? '240px' : 0 },
      }}
    >
      <Box sx={{ maxWidth: 980, mx: 'auto', px: { xs: 3, md: 4 }, py: { xs: 5, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 4, md: 6 }}
          sx={{
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack
              direction="row"
              spacing={1.25}
              sx={{
                alignItems: 'center',
              }}
            >
              <img src="/logomark-color.svg" alt="logo" height="28" />
              <Typography sx={{ color: 'primary.main', fontWeight: 800, fontSize: 18 }}>{serviceName}</Typography>
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1.5, lineHeight: 1.7 }}>
              シンプルでお手頃なIoT罠管理サービス。
              <br />
              見回りの負担を、テクノロジーで軽減します。
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 5 }} sx={{ flex: 2 }}>
            <FooterColumn title="サービス">
              <FooterLink to={client ? `/clients/${client.id}/inquiries` : '/inquiry'}>お問合せ</FooterLink>
              {!isLoading && !client && <FooterLink to="/inquiry/register">ご利用申請</FooterLink>}
              <FooterLink to="/news">ニュース</FooterLink>
              <FooterLink to="/help">ヘルプ</FooterLink>
            </FooterColumn>
            <FooterColumn title="運営">
              <FooterLink href={corporateSiteUrl} external>
                運営会社
              </FooterLink>
            </FooterColumn>
            <FooterColumn title="法的情報">
              <FooterLink to="/public/termsOfUse">利用規約</FooterLink>
              <FooterLink to="/public/tokuteishoutorihikihou">特定商取引法に基づく表記</FooterLink>
            </FooterColumn>
          </Stack>
        </Stack>
      </Box>
      <Box sx={{ borderTop: '1px solid', borderTopColor: 'divider' }}>
        <Box sx={{ maxWidth: 980, mx: 'auto', px: { xs: 3, md: 4 }, py: 2.5 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 2 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              © {new Date().getFullYear()} 株式会社Xenon
            </Typography>
            {/* 古物営業法 12 条 2 項の表示義務。トップページに出す必要があるので全ページの footer に置く */}
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              古物商許可 {kobutsuLicenseText}
            </Typography>
          </Stack>
        </Box>
      </Box>
    </Box>
  )
}

type FooterColumnProps = { title: string; children: React.ReactNode }
const FooterColumn = ({ title, children }: FooterColumnProps) => {
  return (
    <Box>
      <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mb: 1.25 }}>
        {title}
      </Typography>
      <Stack spacing={1}>{children}</Stack>
    </Box>
  )
}

type FooterLinkProps = { children: React.ReactNode; to?: string; href?: string; external?: boolean }
const FooterLink = ({ children, to, href, external }: FooterLinkProps) => {
  const sx = {
    fontSize: 14,
    color: 'text.primary',
    textDecoration: 'none',
    transition: 'color .15s',
    '&:hover': { color: 'primary.dark' },
  } as const
  if (to) {
    return (
      <Typography component={Link} to={to} target={external ? '_blank' : undefined} sx={sx}>
        {children}
      </Typography>
    )
  }
  return (
    <Typography component="a" href={href} target={external ? '_blank' : undefined} rel={external ? 'noopener noreferrer' : undefined} sx={sx}>
      {children}
    </Typography>
  )
}
